import { useState } from 'react';
import { Form, Modal } from 'react-bootstrap';
import { useShallow } from 'zustand/react/shallow';
import { Select } from '../Select';
import { useEquipmentStore } from '@/frontend/store/EquipmentStore';
import { I18n } from '@/i18n/I18n';
import { useProcedureInstanceStore } from '@/frontend/store/ProcedureInstanceStore';

type EquipmentGroupSelectCellProps = {
  equipmentId: string;
};

export function EquipmentGroupSelectCell(props: EquipmentGroupSelectCellProps) {
  const { equipmentId } = props;
  const procedure = useProcedureInstanceStore((s) => s.procedureInstance);
  const equipmentGroups = useEquipmentStore((s) => s.equipmentGroups);
  const assignment = useEquipmentStore(useShallow((s) => s.equipmentGroupAssignments.find((a) => a.equipmentId === equipmentId)));
  const selectedGroup = equipmentGroups.find((g) => g.id === assignment?.groupId);

  const createEquipmentGroup = useEquipmentStore((s) => s.createEquipmentGroup);
  const createEquipmentGroupAssignment = useEquipmentStore((s) => s.createEquipmentGroupAssignment);
  const deleteEquipmentGroupAssignment = useEquipmentStore((s) => s.deleteEquipmentGroupAssignment);

  const [showModal, setShowModal] = useState(false);
  const [newGroupTitle, setNewGroupTitle] = useState('');


  if (!procedure) return null;

  const applyGroupSelection = async (groupId: string) => {
    if (assignment) {
      const response = await deleteEquipmentGroupAssignment(assignment.id);
      if (response.status !== 200) return;
    }
    await createEquipmentGroupAssignment({ equipmentId, groupId });
  };

  const removeAssignment = async () => {
    if (assignment) {
      await deleteEquipmentGroupAssignment(assignment.id);
    }
  };

  const handleCreateGroupConfirm = async () => {
    if (!newGroupTitle.trim()) return;
    const response = await createEquipmentGroup({ title: newGroupTitle.trim(), procedureInstanceId: procedure.id });

    if (response.status !== 201 || !response.equipmentGroup) return;
    await applyGroupSelection(response.equipmentGroup.id);
    setShowModal(false);
    setNewGroupTitle('');
  };

  return (
    <>
      <Select label={I18n.get('group')} value={selectedGroup?.title || I18n.get('none')} minWidth="140px">
        {assignment &&  <button type="button" className="dropdown-item" onClick={() => void removeAssignment()}>
          {I18n.get('none')}
        </button>}
        {equipmentGroups.filter((g) => g.id !== assignment?.groupId).map((g) => (
          <button type="button" key={g.id} className="dropdown-item" onClick={() => void applyGroupSelection(g.id)}>
            {g.title}
          </button>
        ))}
        <div className="dropdown-divider" />
        <button type="button" className="dropdown-item text-center fw-semibold" onClick={() => setShowModal(true)}>
          {I18n.get('plusEquipmentGroup')}
        </button>
      </Select>

      {showModal && (
        <Modal size="sm" show onHide={() => { setShowModal(false); setNewGroupTitle(''); }}>
          <Modal.Body>
            <Modal.Title className="fusa-modal-title">{I18n.get('addEquipmentGroup')}</Modal.Title>
            <Form.Group>
              <Form.Label className="form-label-neutral">{I18n.get('title')}</Form.Label>
              <Form.Control
                className="form-control-neutral"
                value={newGroupTitle}
                onChange={(e) => setNewGroupTitle(e.target.value)}
                autoFocus
              />
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <button type="button" className="fusa-btn-medium btn-blue-light" onClick={() => { setShowModal(false); setNewGroupTitle(''); }}>{I18n.get('cancel')}</button>
            <button type="button" className="fusa-btn-medium fusa-btn-primary" onClick={() => void handleCreateGroupConfirm()} disabled={!newGroupTitle.trim()}>
              {I18n.get('confirm')}
            </button>
          </Modal.Footer>
        </Modal>
      )}
    </>
  );
}
